import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, Navigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from './context/AuthContext.js';
import Navbar from './components/Navbar.js';
import Home from './components/Home.js';
import { StatsRedirect, AthletePerfRedirect, AthleteProfileRedirect } from './components/AnalyticsRedirects.jsx';
import More from './components/More.js';
import RosterOverview from './components/RosterOverview.jsx';
import Profile from './components/Profile.js';
import CreateAthlete from './components/CreateAthlete.js';
import EditAthlete from './components/EditAthlete.js';
import Login from './components/Login.js';
import BottomNav from './components/BottomNav.js';
import ClassesHub from './components/ClassesHub.jsx';
import WaiverManagement from './components/WaiverManagement.jsx';
import WaiverSigningPortal from './components/WaiverSigningPortal.jsx';
import InsuranceManagement from './components/InsuranceManagement.jsx';
import CommunicationHub from './components/CommunicationHub.jsx';
import MedicalProfile from './components/MedicalProfile.jsx';
import MeetCoordination from './components/MeetCoordination.jsx';
import BillingFinance from './components/BillingFinance.jsx';
import TeamCalendar from './components/TeamCalendar.js';
import DashboardRouter from './components/DashboardRouter.jsx';
import InvoiceBilling from './components/InvoiceBilling.jsx';
import SkillTracker from './components/SkillTracker.jsx';
import LandingReview from './components/LandingReview.js';
import BatchClassification from './components/BatchClassification.js';
import UserManagement from './components/UserManagement.jsx';
import AdminParents from './components/AdminParents.jsx';
import AdminAthleteAccounts from './components/AdminAthleteAccounts.jsx';
import AthleteHome from './components/AthleteHome.jsx';
import AthleteProfileHub from './components/AthleteProfileHub.jsx';
import CoachToday from './components/CoachToday.jsx';
import ParentDashboard from './components/ParentDashboard.jsx';
import SuperadminDashboard from './components/SuperadminDashboard.jsx';
import LinkParentApproval from './components/LinkParentApproval.jsx';
import ForgotPassword from './components/ForgotPassword.jsx';
import ResetPassword from './components/ResetPassword.jsx';
import ModelMonitoring from './components/ModelMonitoring.js';
import LabelVerification from './components/LabelVerification.jsx';
import StaffHub from './components/StaffHub.jsx';
import PracticeLogistics from './components/PracticeLogistics.jsx';
import AthleteRegistration from './components/AthleteRegistration.jsx';
import VideoAnalysis from './pages/VideoAnalysis.js';
import VideoDisplay from './pages/VideoDisplay.js';
import GoalSetting from './components/GoalSetting.jsx';
import CoachGoalReview from './components/CoachGoalReview.jsx';
import PerformanceHub from './components/PerformanceHub.jsx';
import WellnessAnalyticsDashboard from './components/WellnessAnalyticsDashboard.jsx';
import WellnessCheckIn from './pages/WellnessCheckIn.jsx';
import CustomFormBuilder from './components/CustomFormBuilder.jsx';
import CustomFormReports from './components/CustomFormReports.jsx';
import Surveys from './pages/Surveys.jsx';
import './styles.css';
import { API_BASE_URL } from './config.js';

// JWT interceptor lives in src/api/index.js — no duplicate needed here.

// RoleProtectedRoute — reads from AuthContext, not localStorage
const RoleProtectedRoute = ({ children, allowedRoles }) => {
  const { user } = useAuth();
  if (!user?.token) return <Navigate to="/login" />;
  if (user.role === 'superadmin' || user.role === 'staff') return children;
  if (!allowedRoles.includes(user.role)) return <Navigate to="/" />;
  return children;
};

function App() {
  const { user, logout } = useAuth();
  const authenticated = !!user?.token;

  const [athletes, setAthletes] = useState([]);
  const [loadingAthletes, setLoadingAthletes] = useState(true);

  // Load athletes after login (uses user from AuthContext, not localStorage)
  useEffect(() => {
    let cancelled = false;

    const loadAthletes = async () => {
      setLoadingAthletes(true);

      if (!user?.token) {
        setAthletes([]);
        setLoadingAthletes(false);
        return;
      }

      try {
        const response = await axios.get(`${API_BASE_URL}/athletes`, {
          headers: { Authorization: `Bearer ${user.token}` },
          timeout: 8000,
        });
        if (cancelled) return;
        setAthletes(response.data || []);
      } catch (err) {
        console.error('Error loading athletes:', err?.response || err.message || err);
        if (err.response?.status === 401) {
          logout();
        } else {
          console.warn('Unable to reach backend. Confirm API_BASE_URL and backend availability.');
        }
      } finally {
        if (!cancelled) setLoadingAthletes(false);
      }
    };

    loadAthletes();
    return () => { cancelled = true; };
  }, [authenticated]); // eslint-disable-line react-hooks/exhaustive-deps

  // ---------------------------
  // Add new athlete
  // ---------------------------
  const addAthlete = async (newAthlete) => {
    try {
      const response = await axios.post(`${API_BASE_URL}/athletes`, newAthlete);
      setAthletes((prev) => [...prev, response.data]);
    } catch (err) {
      console.error("Error adding athlete:", err);
      alert(err.response?.data?.error || "Failed to add athlete.");
    }
  };

  // ---------------------------
  // Delete athlete
  // ---------------------------
  const deleteAthlete = async (id) => {
    try {
      await axios.delete(`${API_BASE_URL}/athletes/${id}`);
      setAthletes((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      console.error('Error deleting athlete:', err);
      alert(err.response?.data?.error || "Failed to delete athlete.");
    }
  };

  // ---------------------------
  // Render loading / auth state
  // ---------------------------
  if (!authenticated) {
    return (
      <Router>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Public gym self-signup REMOVED — gyms are onboarded only by superadmin
              via the RMT Console (POST /api/gym/register is now superadmin-gated). */}
          <Route path="/gym-signup" element={<Navigate to="/login" replace />} />
          <Route path="/register" element={<Navigate to="/login" replace />} />
          {/* Public self-serve enrollment REMOVED — families can no longer attach
              themselves to a gym. Registration is staff-only via the in-gym kiosk. */}
          <Route path="/enroll/:teamId" element={<Navigate to="/login" replace />} />
          <Route path="/enroll" element={<Navigate to="/login" replace />} />
          <Route path="/link-parent/:token" element={<LinkParentApproval />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </Router>
    );
  }

  if (loadingAthletes) {
    return <div className="loading">Loading athletes...</div>;
  }

  return (
    <Router>
      <div>
        <Navbar />
        <Routes>
          {/* HOME - Role-specific dashboard */}
          <Route path="/" element={<DashboardRouter athletes={athletes} />} />
          <Route path="/home" element={<Home athletes={athletes} />} />

          {/* TODAY - coach/admin daily operational home */}
          <Route path="/today" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'superadmin', 'staff']}>
              <CoachToday />
            </RoleProtectedRoute>
          } />

          {/* OPS - gym-wide operations & compliance cockpit */}
          {/* Ops & Compliance merged into the unified Roster */}
          <Route path="/ops" element={<Navigate to="/athletes" replace />} />

          {/* BILLING & FINANCE hub - unified Overview / Invoices / Plans / Accounting */}
          <Route path="/billing" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'superadmin', 'staff']}>
              <BillingFinance />
            </RoleProtectedRoute>
          } />

          {/* REPORTS - now the hub's Overview tab (revenue, A/R aging, enrollment, attendance) */}
          <Route path="/reports" element={
            <RoleProtectedRoute allowedRoles={['admin', 'superadmin', 'staff']}>
              <BillingFinance initialTab="overview" />
            </RoleProtectedRoute>
          } />

          {/* VIDEO ANALYSIS - Primary feature: record, review, pose analysis */}
          <Route path="/video" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent', 'superadmin']}>
              <VideoAnalysis />
            </RoleProtectedRoute>
          } />

          {/* RECORD - Legacy route, redirects to new video page */}
          <Route path="/record" element={<Navigate to="/video" replace />} />

          {/* DISPLAY - Full-screen review wall for TVs / spare iPads */}
          <Route path="/display" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'superadmin']}>
              <VideoDisplay />
            </RoleProtectedRoute>
          } />

          {/* STATS - Unified Stats Page */}
          {/* Stats consolidated into the Performance hub (staff) / profile (athlete) */}
          <Route path="/stats" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent']}>
              <StatsRedirect />
            </RoleProtectedRoute>
          } />

          {/* ROSTER - unified directory + ops/compliance + intake status (admin & coach) */}
          <Route path="/athletes" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <RosterOverview onDelete={deleteAthlete} />
            </RoleProtectedRoute>
          } />

          {/* MORE - Settings and Tools */}
          <Route path="/more" element={<More />} />

          {/* Profile page */}
          <Route path="/profile/:id" element={<Profile athletes={athletes} />} />
          <Route path="/profile" element={<Profile athletes={athletes} />} />

          {/* Create Athlete */}
          <Route path="/create-athlete" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <CreateAthlete addAthlete={addAthlete} />
            </RoleProtectedRoute>
          } />

          {/* Legacy full profile consolidated into the unified hub (carries ?tab/&date) */}
          <Route path="/athlete/:id" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent']}>
              <AthleteProfileRedirect />
            </RoleProtectedRoute>
          } />

          {/* Athlete Profile - unified per-athlete view (Overview / Performance /
              Wellness tabs); backend scopes access by role */}
          <Route path="/athlete-card/:id" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent', 'superadmin', 'staff']}>
              <AthleteProfileHub />
            </RoleProtectedRoute>
          } />

          {/* Parent household is now the action-first home dashboard */}
          <Route path="/parent/athletes" element={<Navigate to="/" replace />} />

          {/* Edit Athlete */}
          <Route path="/edit-athlete/:id" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <EditAthlete athletes={athletes} setAthletes={setAthletes} />
            </RoleProtectedRoute>
          } />

          {/* Athlete Performance - admin, coach, athlete */}
          {/* Superseded by the profile Performance tab */}
          <Route path="/athlete-performance/:id" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent']}>
              <AthletePerfRedirect />
            </RoleProtectedRoute>
          } />

          {/* Classes hub - unified Class Management + Attendance (admin & coach) */}
          <Route path="/classes-hub" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <ClassesHub />
            </RoleProtectedRoute>
          } />
          {/* Back-compat: Class Management is now the hub's Classes tab */}
          <Route path="/class-management" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <ClassesHub initialTab="classes" />
            </RoleProtectedRoute>
          } />

          {/* Back-compat: Attendance is now the hub's Attendance tab */}
          <Route path="/attendance" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <ClassesHub initialTab="attendance" />
            </RoleProtectedRoute>
          } />

          {/* Waiver Management - admin & coach */}
          <Route path="/waivers" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <WaiverManagement />
            </RoleProtectedRoute>
          } />

          {/* Waiver Signing - parents sign their athletes' required waivers */}
          <Route path="/sign-waivers" element={
            <RoleProtectedRoute allowedRoles={['parent']}>
              <WaiverSigningPortal />
            </RoleProtectedRoute>
          } />

          {/* Insurance & Liability - admin & coach */}
          <Route path="/insurance" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <InsuranceManagement />
            </RoleProtectedRoute>
          } />

          {/* Communication Hub - all roles (parents/athletes are read-only in the component) */}
          <Route path="/communication" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'parent', 'athlete']}>
              <CommunicationHub />
            </RoleProtectedRoute>
          } />

          {/* Medical Profile - admin & coach */}
          <Route path="/medical-profile" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <MedicalProfile />
            </RoleProtectedRoute>
          } />

          {/* Meet Coordination - admin & coach */}
          <Route path="/meet-coordination" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <MeetCoordination />
            </RoleProtectedRoute>
          } />

          {/* Team Calendar - all roles */}
          <Route path="/calendar" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent']}>
              <TeamCalendar />
            </RoleProtectedRoute>
          } />

          {/* Financial Reports - now the hub's Accounting tab (admin only) */}
          <Route path="/financial-reports" element={
            <RoleProtectedRoute allowedRoles={['admin']}>
              <BillingFinance initialTab="accounting" />
            </RoleProtectedRoute>
          } />

          {/* Invoice Management - admin/coach manage, parents pay */}
          <Route path="/invoices" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'parent']}>
              <InvoiceBilling />
            </RoleProtectedRoute>
          } />

          {/* Parent Dashboard + Portal */}
          <Route path="/parent/dashboard" element={
            <RoleProtectedRoute allowedRoles={['parent']}>
              <ParentDashboard />
            </RoleProtectedRoute>
          } />
          <Route path="/parent/invoices" element={
            <RoleProtectedRoute allowedRoles={['parent']}>
              <InvoiceBilling />
            </RoleProtectedRoute>
          } />

          {/* Skill Tracker — HIDDEN from nav for beta (deferred until fed by the ML
              pipeline). Routes kept intact so the feature can be re-surfaced later. */}
          <Route path="/skills" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent']}>
              <SkillTracker />
            </RoleProtectedRoute>
          } />

          {/* Skill Tracker - individual athlete */}
          <Route path="/skills/:athleteId" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach', 'athlete', 'parent']}>
              <SkillTracker />
            </RoleProtectedRoute>
          } />

          {/* Goal Setting - athlete sets and tracks goals */}
          <Route path="/goals" element={
            <RoleProtectedRoute allowedRoles={['athlete', 'coach', 'admin']}>
              <GoalSetting />
            </RoleProtectedRoute>
          } />

          {/* Coach Goal Review - coaches monitor athlete goals and provide feedback */}
          <Route path="/goals/review" element={
            <RoleProtectedRoute allowedRoles={['coach', 'admin']}>
              <CoachGoalReview />
            </RoleProtectedRoute>
          } />

          {/* Performance hub - Practice Tally + History + Practice Analytics (under Development) */}
          <Route path="/performance" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <PerformanceHub />
            </RoleProtectedRoute>
          } />
          {/* Back-compat: Practice Analytics is now the hub's Analytics tab */}
          <Route path="/practice-analytics" element={
            <RoleProtectedRoute allowedRoles={['coach', 'admin']}>
              <PerformanceHub initialTab="analytics" />
            </RoleProtectedRoute>
          } />

          {/* Landing Review - MODEL LABELING (superadmin/RMT only, not gym staff) */}
          <Route path="/landing-review" element={
            <RoleProtectedRoute allowedRoles={['superadmin', 'staff']}>
              <LandingReview />
            </RoleProtectedRoute>
          } />

          {/* Batch Classification - MODEL LABELING (superadmin/RMT only) */}
          <Route path="/batch-classification" element={
            <RoleProtectedRoute allowedRoles={['superadmin', 'staff']}>
              <BatchClassification />
            </RoleProtectedRoute>
          } />

          {/* Admin User Management */}
          <Route path="/admin/users" element={
            <RoleProtectedRoute allowedRoles={['admin']}>
              <UserManagement />
            </RoleProtectedRoute>
          } />

          {/* Admin: parent onboarding + linking */}
          <Route path="/admin/parents" element={
            <RoleProtectedRoute allowedRoles={['admin']}>
              <AdminParents />
            </RoleProtectedRoute>
          } />

          {/* Admin: athlete login accounts */}
          <Route path="/admin/athlete-accounts" element={
            <RoleProtectedRoute allowedRoles={['admin']}>
              <AdminAthleteAccounts />
            </RoleProtectedRoute>
          } />

          {/* Athlete self-view home */}
          <Route path="/athlete/home" element={
            <RoleProtectedRoute allowedRoles={['athlete']}>
              <AthleteHome />
            </RoleProtectedRoute>
          } />

          {/* Model Monitoring - MODEL DEV (superadmin/RMT only, not gym admin) */}
          <Route path="/model-monitoring" element={
            <RoleProtectedRoute allowedRoles={['superadmin', 'staff']}>
              <ModelMonitoring />
            </RoleProtectedRoute>
          } />

          <Route path="/verify-labels" element={
            <RoleProtectedRoute allowedRoles={['superadmin', 'staff']}>
              <LabelVerification />
            </RoleProtectedRoute>
          } />

          {/* Staff hub - unified Schedule + Time Clock (admin manages, coaches view) */}
          <Route path="/staff" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <StaffHub />
            </RoleProtectedRoute>
          } />
          {/* Back-compat: Staff Scheduling is now the hub's Schedule tab */}
          <Route path="/staff-scheduling" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <StaffHub initialTab="schedule" />
            </RoleProtectedRoute>
          } />

          {/* Practice Logistics - design rotations, themes, goals, workout per practice */}
          <Route path="/practice" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <PracticeLogistics />
            </RoleProtectedRoute>
          } />

          {/* Onsite Registration kiosk - create athlete + parent + link + insurance + waivers */}
          <Route path="/kiosk" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <AthleteRegistration />
            </RoleProtectedRoute>
          } />
          {/* Back-compat: old kiosk path */}
          <Route path="/register" element={<Navigate to="/kiosk" replace />} />

          {/* Roster intake progress dashboard */}
          {/* Roster Intake merged into the unified Roster */}
          <Route path="/intake" element={<Navigate to="/athletes" replace />} />

          {/* Back-compat: Time Clock is now the hub's Time Clock tab */}
          <Route path="/time-clock" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <StaffHub initialTab="timeclock" />
            </RoleProtectedRoute>
          } />

          {/* Payment Plans - now the hub's Plans & Recurring tab (admin only) */}
          <Route path="/payment-plans" element={
            <RoleProtectedRoute allowedRoles={['admin']}>
              <BillingFinance initialTab="plans" />
            </RoleProtectedRoute>
          } />

          {/* Admin dashboard consolidated into the home dashboard (Quick Actions +
              Alerts folded in); this route now redirects there */}
          <Route path="/admin/dashboard" element={<Navigate to="/" replace />} />

          {/* Wellness Dashboard - team wellness trends */}
          <Route path="/wellness" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <WellnessAnalyticsDashboard />
            </RoleProtectedRoute>
          } />

          {/* Body-map soreness is now a unified check-in question — team view is
              the wellness dashboard heat map; athlete input is the Daily Check-In */}
          <Route path="/wellness/team-health" element={<Navigate to="/wellness" replace />} />

          {/* Custom Forms - drag-and-drop form builder */}
          <Route path="/wellness/forms" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <CustomFormBuilder />
            </RoleProtectedRoute>
          } />
          {/* Custom Form responses reporting (e.g. monthly wellness check) */}
          <Route path="/wellness/forms/reports" element={
            <RoleProtectedRoute allowedRoles={['admin', 'coach']}>
              <CustomFormReports />
            </RoleProtectedRoute>
          } />
          {/* Athlete-facing survey fill screen (periodic custom forms) */}
          <Route path="/wellness/surveys" element={
            <RoleProtectedRoute allowedRoles={['athlete', 'parent', 'admin', 'coach']}>
              <Surveys />
            </RoleProtectedRoute>
          } />

          {/* Recovery Trends duplicated the team wellness dashboard — collapse it */}
          <Route path="/wellness/analytics" element={<Navigate to="/wellness" replace />} />

          {/* Athlete body-map self-assessment is now part of the Daily Check-In */}
          <Route path="/wellness/athlete" element={<Navigate to="/wellness/checkin" replace />} />

          {/* Daily Check-In - quick readiness self-report */}
          <Route path="/wellness/checkin" element={
            <RoleProtectedRoute allowedRoles={['athlete', 'parent', 'coach', 'admin']}>
              <WellnessCheckIn />
            </RoleProtectedRoute>
          } />

          {/* Readiness board folded into the team wellness dashboard + Today flags */}
          <Route path="/wellness/readiness" element={<Navigate to="/wellness" replace />} />

          {/* Parent Payment Portal - parents view/pay invoices */}
          {/* Legacy standalone portal (own login + non-functional pay) retired →
              real invoice view. */}
          <Route path="/payment-portal" element={<Navigate to="/parent/invoices" replace />} />

          {/* RMT Superadmin Console */}
          <Route path="/superadmin" element={
            <RoleProtectedRoute allowedRoles={['superadmin', 'staff']}>
              <SuperadminDashboard />
            </RoleProtectedRoute>
          } />

          {/* Legacy route redirects for backward compatibility */}
          <Route path="/training-stats" element={<Navigate to="/stats" replace />} />
          <Route path="/coach-dashboard" element={<Navigate to="/" replace />} />

          {/* Catch-all */}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <BottomNav />
      </div>
    </Router>
  );
}

export default App;
